import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import HoverTooltip from "../HoverTooltip.jsx";
import { NOTE_CLASS } from "../../lib/ui.js";
import { fetchCheckStatus, fetchTriageStart } from "../../lib/findingsTriage.js";
import { GROUP_LABEL, GROUP_ORDER, OPEN_GROUPS, SET_ASIDE, placeFindings } from "../../lib/investigateFacts.js";
import { buildCases, unorganized } from "../../lib/investigateCases.js";
import { hasInvestigationWork, useInvestigation } from "../../lib/investigateStore.js";
import {
  CHECK_MAX, ORGANIZE_GROUPS, dispatch, keepCase, provideInvestigateContext, snapshotOf, startSwarm, stopSwarm, swarmOf, useSwarm,
  workingOn,
} from "../../lib/investigateSwarm.js";
import { askCoordinator, bindKeptCoordinator, canCoordinate, openCoordinator, startFresh, startInvestigation } from "../../lib/investigateCoordinator.js";
import { openCaseOnWorkspace } from "../../lib/investigateWorkspace.js";
import { caseChatOf, openCaseChat } from "../../lib/investigateChat.js";
import { getTriageChatHost, subscribeTriageChatHost } from "../../lib/triageChatStore.js";
import { severityRank } from "../../lib/triageLite.js";
import InvestigateCase from "./InvestigateCase.jsx";
import InvestigateSuggestions from "./InvestigateSuggestions.jsx";
import { SwarmControls } from "./InvestigateSwarm.jsx";
import { GroupHead, PendingCases } from "./InvestigateParts.jsx";
import { machineOf, useSessionIdentity } from "./reviewSessions.jsx";
import { ACTION_CHIP } from "./triageParts.jsx";

// The Reviews lens's Investigate tab. Where each finding stands comes from
// the backend check's facts and the standards' answers (investigateFacts):
// what needs a person, what slipped past the standards, what is still open,
// and what was set aside and why. Investigate's own work sits on top, done
// by investigators the reader starts (lib/investigateSwarm.js) and a chat
// beside the tab coordinates (lib/investigateCoordinator.js): they fold the
// findings into cases, dig into the ones that matter (the sessions, and what
// actually ran on the machine), note what the review and the check missed or
// got wrong, check findings the backend never did, and draft standard edits
// from what slipped past. The investigation happens in that chat: the tab
// keeps the cases, each with what is at work on it, and says the reader's
// requests (dig this, check those) in the chat as theirs. A case goes to the
// trace workspace with one click, its notes pinned at their moments.

// A case dug at the reader's word: a set-aside one is double-checked.
const digItem = (view, question = "") => ({ kind: SET_ASIDE.has(view.group) ? "double_check" : "dig", caseId: view.id, findingIds: view.findingIds, question });
// The same, said in the investigation's chat.
const digPrompt = (view, question = "") => (SET_ASIDE.has(view.group)
  ? `Double-check \u201c${view.title}\u201d: does setting it aside hold?${question ? ` ${question}` : ""} (case ${view.id})`
  : `Dig into \u201c${view.title}\u201d${question ? `: ${question}` : "."} (case ${view.id})`);

// Cases a group shows before "show more".
const SHOWN = { needs_you: 50, refused: 50, slipped: 12, unsettled: 8, unchecked: 8, didnt_happen: 6, allowed: 6, contained: 6, not_an_action: 6 };
const str = (v) => (v == null ? "" : String(v));
// When earlier work was last done, as the app's lists date things.
const whenText = (ms) => new Date(ms).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
// A count in the tab's head: the number in ink, what it counts muted.
function Count({ n, one, many = `${one}s` }) {
  return <><strong className="font-medium text-[color:var(--ink)]">{n.toLocaleString()}</strong> {n === 1 ? one : many}</>;
}

// When the backend first checked anything near the range, and why the
// findings it has not checked stopped short where it recorded an attempt.
function useCheckBasis({ findings, verdicts, window, enabled }) {
  const [state, setState] = useState({ start: null, statuses: {}, ready: false, for: "" });
  const unchecked = useMemo(() => (findings || []).filter((f) => !verdicts?.[f.id]).map((f) => f.id), [findings, verdicts]);
  const key = `${window?.fromMs}|${window?.toMs}|${unchecked.length}`;
  useEffect(() => {
    if (!enabled || !window?.fromMs) return undefined;
    const controller = new AbortController();
    (async () => {
      const start = await fetchTriageStart({ fromMs: window.fromMs, signal: controller.signal }).catch(() => null);
      // Nothing checked in reach: no attempt to read either.
      const statuses = start == null || !unchecked.length ? {} : await fetchCheckStatus({ ids: unchecked, reviewWindow: window, signal: controller.signal }).catch(() => ({}));
      if (!controller.signal.aborted) setState({ start, statuses, ready: true, for: key });
    })();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);
  return { ...state, ready: state.ready && state.for === key };
}

export default function InvestigateTab({ scopeKey, scopeLabel, lite, std, pool, window, sessionLabel = null, blocked = null, poolLine = null, onOpenSession, onGoTriage = null, focus = null }) {
  const investigation = useInvestigation(scopeKey);
  const swarm = useSwarm(scopeKey);
  const standards = std.lib.items;
  // With one standard kept, the Triage tab's answers carry its digest but not
  // its name: a met finding names the standard that met it either way.
  const verdicts = useMemo(() => {
    const byDigest = new Map(standards.map((x) => [x.digest, `${x.name} v${x.version}`]));
    const out = {};
    for (const [id, v] of Object.entries(std.effective || {})) {
      out[id] = v?.escalates && !v.metBy?.length && byDigest.has(v.standardDigest) ? { ...v, metBy: [byDigest.get(v.standardDigest)] } : v;
    }
    return out;
  }, [std.effective, standards]);
  const findings = lite.pool;
  const ready = pool?.status === "done" && lite.settled && std.answersReady;
  const basis = useCheckBasis({ findings, verdicts: lite.verdicts, window: lite.readWindow || window, enabled: pool?.status === "done" && lite.settled });

  const placed = useMemo(() => placeFindings(findings, {
    verdicts, checks: investigation.checks, corrections: investigation.corrections, asked: investigation.asked,
    standards: standards.length, start: basis.start, statuses: basis.statuses,
  }), [findings, verdicts, investigation.checks, investigation.corrections, investigation.asked, standards.length, basis.start, basis.statuses]);
  const built = useMemo(() => buildCases({ placed, map: lite.map, investigation }), [placed, lite.map, investigation]);
  const placedById = useMemo(() => new Map(placed.map((p) => [str(p.finding.id).toLowerCase(), p])), [placed]);

  // What investigators read when they start, whether or not this tab is up.
  const sessionsShown = useMemo(() => [...new Set(built.cases.slice(0, 300).flatMap((c) => c.sessions))].sort(), [built.cases]);
  const endpointsShown = useMemo(() => [...new Set(built.cases.slice(0, 300).flatMap((c) => c.endpoints))], [built.cases]);
  const { labels, machineFor } = useSessionIdentity({ map: lite.map, sessions: sessionsShown, endpoints: endpointsShown, window, sessionLabel });
  const nameOf = useMemo(() => ({ session: (id) => labels.get(id), machine: (endpoint) => machineOf(endpoint)?.host || null, conduct: lite.conductName }), [labels, lite.conductName]);
  useEffect(() => {
    if (!ready) return;
    provideInvestigateContext(scopeKey, { pool: findings, map: lite.map, verdicts, standards, start: basis.start, statuses: basis.statuses, window: lite.readWindow || window, nameOf, scopeLabel });
  }, [ready, scopeKey, findings, lite.map, verdicts, standards, basis.start, basis.statuses, lite.readWindow, window, nameOf, scopeLabel]);

  // The chat console, when one is mounted: it holds case chats and the
  // coordinator, and runs the coordinator's turns.
  const host = useSyncExternalStore(subscribeTriageChatHost, getTriageChatHost, () => null);
  const canChat = typeof host?.stage === "function";
  const coordinate = canCoordinate(host);
  // A coordinator this browser kept for the scope hears from investigators
  // again after a reload.
  useEffect(() => { if (coordinate) bindKeptCoordinator({ host, scopeKey }); }, [coordinate, host, scopeKey]);

  // While investigators run, findings that arrive in view are organized as
  // they come; the coordinator hears what that made.
  const seen = useRef({ scopeKey: "", n: 0 });
  useEffect(() => {
    if (!ready || !basis.ready) return;
    const prev = seen.current;
    seen.current = { scopeKey, n: findings.length };
    if (prev.scopeKey !== scopeKey || findings.length <= prev.n) return;
    const s = swarmOf(scopeKey);
    if (!s?.on || s.investigators.some((i) => i.kind === "organize" && (i.status === "queued" || i.status === "running"))) return;
    if (unorganized(built, ORGANIZE_GROUPS).length) dispatch(scopeKey, [{ kind: "organize" }], { by: "auto" });
  }, [ready, basis.ready, scopeKey, findings.length, built]);

  const [openCases, setOpenCases] = useState(() => new Set());
  const [more, setMore] = useState(() => new Set());
  const [asideOpen, setAsideOpen] = useState(() => new Set());
  const [confirmFresh, setConfirmFresh] = useState(false);
  useEffect(() => { setOpenCases(new Set()); setMore(new Set()); setAsideOpen(new Set()); setConfirmFresh(false); }, [scopeKey]);

  // Work this browser kept for the scope from before the page opened: the
  // head says when it was last worked on, and offers a fresh start. Read once
  // the scope's investigation is in, which can be after the first render.
  const openedAt = useRef(Date.now());
  const [carried, setCarried] = useState(null);
  useEffect(() => {
    if (carried?.scopeKey === scopeKey) return;
    if (hasInvestigationWork(investigation) && investigation.at > 0 && investigation.at < openedAt.current) setCarried({ scopeKey, at: investigation.at });
  }, [scopeKey, investigation, carried]);
  const carriedAt = carried?.scopeKey === scopeKey ? carried.at : 0;
  const fresh = useCallback(() => {
    startFresh(scopeKey);
    setCarried({ scopeKey, at: 0 });
    setConfirmFresh(false);
    setOpenCases(new Set());
  }, [scopeKey]);

  // Investigators may run from here on: a dig the reader asks for starts
  // the investigation if it was not running.
  const ensureOn = useCallback(() => { if (!swarmOf(scopeKey)?.on) startSwarm(scopeKey); }, [scopeKey]);
  const toggleCase = useCallback((id) => setOpenCases((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n; }), []);
  // What the reader asks for in the tab goes to the investigation's chat as
  // their message, where a console holds one; else straight to investigators.
  const request = useCallback((prompt, direct) => {
    if (coordinate && askCoordinator({ host, scopeKey, scopeLabel, prompt })) return;
    ensureOn();
    direct();
  }, [coordinate, host, scopeKey, scopeLabel, ensureOn]);
  // A case asked about is a stored one, so the chat names it by an id that
  // lasts; one opened here stays open under its new id.
  const stored = useCallback((view) => {
    const id = keepCase(scopeKey, view);
    if (id === view.id) return view;
    setOpenCases((cur) => (cur.has(view.id) ? new Set(cur).add(id) : cur));
    return snapshotOf(scopeKey)?.built.byId.get(id) || { ...view, id };
  }, [scopeKey]);

  // Cases opened where they sit and brought into view: their groups shown
  // in full, set-aside ones unfolded.
  const wrapRef = useRef(null);
  const reveal = useCallback((views) => {
    if (!views.length) return;
    setOpenCases((cur) => new Set([...cur, ...views.map((c) => c.id)]));
    setMore((cur) => new Set([...cur, ...views.map((c) => c.group)]));
    setAsideOpen((cur) => new Set([...cur, ...views.map((c) => c.group).filter((g) => SET_ASIDE.has(g))]));
    requestAnimationFrame(() => wrapRef.current?.querySelector(`[data-investigate-case="${String(views[0].id).replace(/["\\]/g, "\\$&")}"]`)?.scrollIntoView?.({ block: "start", behavior: "smooth" }));
  }, []);

  // A session sent here from the Findings tab: its cases open where they
  // sit, come into view, and the first is dug unless it has been.
  const focusSeen = useRef(null);
  useEffect(() => {
    if (!focus?.n || focusSeen.current === focus.n || !ready || !basis.ready) return;
    focusSeen.current = focus.n;
    const wanted = new Set(focus.findings || []);
    const hits = built.cases.filter((c) => c.findingIds.some((id) => wanted.has(String(id).toLowerCase())) || c.sessions.includes(String(focus.session || "").toLowerCase()));
    if (!hits.length) return;
    reveal(hits);
    if (!hits[0].dig && !workingOn(swarmOf(scopeKey), hits[0].id)) {
      const view = stored(hits[0]);
      request(digPrompt(view), () => dispatch(scopeKey, [digItem(view)], { by: "you", front: true }));
    }
  }, [focus, ready, basis.ready, built.cases, scopeKey, reveal, stored, request]);

  const start = useCallback(() => startInvestigation({ scopeKey, scopeLabel, host: coordinate ? host : null }), [scopeKey, scopeLabel, host, coordinate]);
  const talk = useCallback(() => { if (coordinate) openCoordinator({ host, scopeKey, scopeLabel }); }, [coordinate, host, scopeKey, scopeLabel]);
  const dig = useCallback((view, question = "") => {
    const kept = stored(view);
    setOpenCases((cur) => new Set(cur).add(kept.id));
    request(digPrompt(kept, question), () => dispatch(scopeKey, [digItem(kept, question)], { by: "you", front: true }));
  }, [scopeKey, stored, request]);
  // A case goes over as a stored case, so its board is kept by it.
  const toWorkspace = useCallback((view) => {
    openCaseOnWorkspace({ scopeKey, view: stored(view), titleOf: (s) => labels.get(s) || "" });
  }, [scopeKey, stored, labels]);
  // A case as a chat: where a chat console can host one, the chat this
  // browser opened on it, or a new one.
  const [, chatTick] = useState(0);
  const chatOf = (view) => {
    const ref = caseChatOf(scopeKey, view.id);
    return ref && host?.has?.(ref) ? ref : null;
  };
  const toChat = useCallback((view) => {
    if (!canChat) return;
    const ref = caseChatOf(scopeKey, view.id);
    if (ref && host.has?.(ref)) { host.open(ref); return; }
    openCaseChat({ host, view: stored(view), scopeKey, window: lite.readWindow || window });
    chatTick((n) => n + 1);
  }, [canChat, host, scopeKey, stored, lite.readWindow, window]);
  const checkLoudest = useCallback((group) => {
    const ids = group.cases.flatMap((c) => c.items).filter((p) => !p.verdict && p.group !== "refused")
      .sort((a, b) => severityRank(b.finding.severity) - severityRank(a.finding.severity))
      .slice(0, CHECK_MAX).map((p) => p.finding.id);
    if (!ids.length) return;
    request(`Check what nobody has checked yet in \u201c${GROUP_LABEL[group.id]}\u201d, loudest first.`, () => dispatch(scopeKey, [{ kind: "check", ids }], { by: "you", front: true }));
  }, [scopeKey, request]);
  const organize = useCallback((groupId) => {
    request(`Organize the findings in \u201c${GROUP_LABEL[groupId]}\u201d into cases.`, () => dispatch(scopeKey, [{ kind: "organize", groups: [groupId] }], { by: "you" }));
  }, [scopeKey, request]);
  const busyWith = (kind) => (swarm?.investigators || []).some((i) => i.kind === kind && (i.status === "queued" || i.status === "running"));

  const caseRow = (view, hero = false) => (
    <InvestigateCase
      key={view.id}
      view={view}
      hero={hero}
      open={openCases.has(view.id)}
      onToggle={() => toggleCase(view.id)}
      labels={labels}
      machineFor={machineFor}
      conductName={lite.conductName}
      activity={workingOn(swarm, view.id)}
      onDig={dig}
      onWorkspace={toWorkspace}
      onOpenSession={onOpenSession}
      onChat={canChat ? toChat : null}
      inChat={canChat && openCases.has(view.id) ? Boolean(chatOf(view)) : false}
    />
  );
  const groupOf = (id) => built.groups.find((g) => g.id === id);
  const listOf = (g) => {
    const limit = more.has(g.id) ? Infinity : SHOWN[g.id] || 8;
    const shown = g.cases.slice(0, limit);
    return (
      <div className="grid min-w-0">
        {shown.map((c) => caseRow(c, g.id === "needs_you" || g.id === "refused"))}
        {g.cases.length > shown.length ? (
          <button type="button" data-investigate-more={g.id} onClick={() => setMore((cur) => new Set(cur).add(g.id))} className={`${ACTION_CHIP} mt-[6px] justify-self-start`}>
            Show {(g.cases.length - shown.length).toLocaleString()} more
          </button>
        ) : null}
      </div>
    );
  };

  if (blocked) return <div data-investigate="" className="grid gap-[8px] pt-[12px]">{blocked}</div>;
  const loading = !ready || !basis.ready;
  const total = placed.length;
  const organizedCases = built.cases.filter((c) => !c.provisional).length;
  const corrected = placed.filter((p) => p.source === "corrected").length;
  const missed = built.cases.reduce((n, c) => n + c.missed, 0);
  const needs = groupOf("needs_you");
  const refused = groupOf("refused");
  const slipped = groupOf("slipped");

  return (
    <div ref={wrapRef} data-investigate="" aria-busy={loading || undefined} className="grid min-w-0 gap-[10px] pt-[12px]">
      <div data-investigate-head="" className="grid gap-[6px] px-[6px]">
        {/* The counts, and beside them the investigation's controls: they
            keep the first line, and the counts wrap in what is left. */}
        <div className="grid min-w-0 items-center gap-x-[16px] gap-y-[4px] [grid-template-columns:minmax(0,1fr)_auto]">
          <span data-investigate-count="" className="min-w-0 text-[11px] leading-[1.3] text-[color:var(--muted)]">
            {loading ? (
              // What is being read, as it comes: the scope's findings, then
              // what the check and the standards made of them.
              pool?.status !== "done" ? <span className="motion-safe:animate-pulse">Reading the scope&rsquo;s findings&hellip;</span> : (
                <>
                  {findings.length ? <><Count n={findings.length} one="finding" /> {"\u00b7"} </> : null}
                  <span className="motion-safe:animate-pulse">reading what Triage checked&hellip;</span>
                </>
              )
            ) : (
              <>
                <Count n={total} one="finding" />
                {organizedCases ? <> {"\u00b7"} <Count n={organizedCases} one="case" /></> : null}
                {missed ? (
                  <> {"\u00b7"} <HoverTooltip label={`${missed} note${missed === 1 ? "" : "s"} on what the review and its check missed or got wrong`} placement="top"><span data-investigate-missed-total=""><Count n={missed} one="missed" many="missed" /></span></HoverTooltip></>
                ) : null}
                {corrected ? (
                  <> {"\u00b7"} <HoverTooltip label={`Investigate corrected the check on ${corrected} finding${corrected === 1 ? "" : "s"}, from what the sessions and the machines' records showed`} placement="top"><span data-investigate-corrected=""><Count n={corrected} one="corrected" many="corrected" /></span></HoverTooltip></>
                ) : null}
              </>
            )}
          </span>
          <SwarmControls
            swarm={swarm}
            canCoordinate={coordinate}
            reading={loading}
            spentHere={investigation.spentUsd}
            onStart={start}
            onStop={() => stopSwarm(scopeKey)}
            onTalk={talk}
          />
        </div>
        {!loading && carriedAt ? (
          <span data-investigate-carried="" className="flex flex-wrap items-center gap-x-[8px] gap-y-[4px]">
            <span className={NOTE_CLASS}>Includes your earlier investigation, last worked on {whenText(carriedAt)}: its cases, digs and notes.</span>
            {swarm?.on ? null : confirmFresh ? (
              <>
                <span className={NOTE_CLASS}>Clear them from this browser?</span>
                <button type="button" data-investigate-fresh-confirm="" onClick={fresh} className={`${ACTION_CHIP} border-[color:var(--line-strong)] text-[color:var(--ink)]`}>Clear</button>
                <button type="button" data-investigate-fresh-keep="" onClick={() => setConfirmFresh(false)} className={ACTION_CHIP}>Keep</button>
              </>
            ) : (
              <HoverTooltip label="Clears the cases, digs, notes, corrections and priorities this browser kept for this scope, so the next investigation starts from what the backend found. Its chat stays in your chats." placement="top" wide>
                <button type="button" data-investigate-fresh="" onClick={() => setConfirmFresh(true)} className={ACTION_CHIP}>Start fresh</button>
              </HoverTooltip>
            )}
          </span>
        ) : null}
        {!loading && pool?.hasMore ? (
          <span className="flex flex-wrap items-center gap-[8px]">
            <span className={NOTE_CLASS}>These are the newest findings in range; older ones aren&rsquo;t loaded yet.</span>
            <button type="button" data-investigate-older="" disabled={pool.loadingMore} onClick={pool.loadMore} className={`${ACTION_CHIP} disabled:opacity-50`}>
              {pool.loadingMore ? "Loading older findings\u2026" : "Load older findings"}
            </button>
          </span>
        ) : null}
        {poolLine}
      </div>
      {loading ? (
        // The tab as it will stand, its groups' rows held until they are read.
        <div data-investigate-reading="" className="grid min-w-0">
          <div data-investigate-pending-section="needs_you" className="grid min-w-0">
            <GroupHead group="needs_you" />
            <PendingCases rows={2} />
          </div>
          <div data-investigate-pending-section="slipped" className="grid min-w-0">
            <GroupHead group="slipped" />
            <PendingCases rows={3} />
          </div>
          <div data-investigate-pending-section="set-aside" className="grid min-w-0 pt-[8px]">
            <span className="px-[6px] pt-[8px] pb-[6px] text-[10px] leading-[1.2] tracking-[0.08em] uppercase text-[color:var(--muted)]">Set aside</span>
            <PendingCases rows={1} />
          </div>
        </div>
      ) : (
        <div data-investigate-groups="" className="grid min-w-0">
          <section data-investigate-section="needs_you" className="grid min-w-0">
            <GroupHead group="needs_you" cases={needs.cases.length} findings={needs.findings} elsewhere={needs.elsewhere} moved={needs.moved} reasons={needs.reasons} />
            {needs.cases.length ? listOf(needs) : (
              <span data-investigate-none="" className="px-[6px] pb-[6px] text-[12px] leading-[1.35] text-[color:var(--ink)]">
                {standards.length ? "Nothing met a standard in this range." : "No standards are set, so nothing is marked as needing you."}
                {!standards.length && onGoTriage ? <> <button type="button" onClick={onGoTriage} className={`${ACTION_CHIP} ml-[6px]`}>Set up standards</button></> : null}
              </span>
            )}
          </section>

          {refused.cases.length || refused.findings ? (
            <section data-investigate-section="refused" className="grid min-w-0">
              <GroupHead group="refused" cases={refused.cases.length} findings={refused.findings} elsewhere={refused.elsewhere} reasons={refused.reasons} />
              {listOf(refused)}
            </section>
          ) : null}

          <section data-investigate-section="slipped" className="grid min-w-0">
            <GroupHead group="slipped" cases={slipped.cases.length} findings={slipped.findings} elsewhere={slipped.elsewhere} moved={slipped.moved} reasons={slipped.reasons} />
            <InvestigateSuggestions scopeKey={scopeKey} suggestions={investigation.suggestions} std={std} placedById={placedById} window={lite.readWindow || window} onAdded={() => {}} />
            {slipped.cases.length ? listOf(slipped) : <span className={`${NOTE_CLASS} px-[6px] pb-[6px]`}>Nothing real slipped past the standards here.</span>}
          </section>

          {/* A group whose findings all sit in cases a group above leads still
              says so, as the flow counts them: its head, with nothing to list. */}
          {GROUP_ORDER.filter((id) => OPEN_GROUPS.has(id)).map((id) => {
            const g = groupOf(id);
            if (!g.cases.length && !g.findings) return null;
            const action = id === "unchecked" && g.cases.some((c) => c.items.some((p) => !p.verdict))
              ? (
                <HoverTooltip label="Reads each session and settles the same five facts the backend check does, under its rules, then places them. Marked as checked here." placement="top" wide>
                  <button type="button" data-investigate-check="" disabled={busyWith("check")} onClick={() => checkLoudest(g)} className={`${ACTION_CHIP} disabled:opacity-50`}>Check the {Math.min(CHECK_MAX, g.findings)} loudest here</button>
                </HoverTooltip>
              ) : null;
            return (
              <section key={id} data-investigate-section={id} className="grid min-w-0">
                <GroupHead group={id} cases={g.cases.length} findings={g.findings} elsewhere={g.elsewhere} moved={g.moved} reasons={g.reasons} action={action} quiet />
                {listOf(g)}
              </section>
            );
          })}

          <section data-investigate-section="set-aside" className="grid min-w-0 pt-[8px]">
            <span className="px-[6px] pt-[8px] pb-[2px] text-[10px] leading-[1.2] tracking-[0.08em] uppercase text-[color:var(--muted)]">
              Set aside {"\u00b7"} {GROUP_ORDER.filter((id) => SET_ASIDE.has(id)).reduce((n, id) => n + groupOf(id).findings, 0).toLocaleString()} findings
            </span>
            {GROUP_ORDER.filter((id) => SET_ASIDE.has(id)).map((id) => {
              const g = groupOf(id);
              if (!g.cases.length && !g.findings) return null;
              const isOpen = asideOpen.has(id);
              const loose = g.cases.some((c) => c.provisional);
              return (
                <div key={id} data-investigate-section={id} className="grid min-w-0">
                  <GroupHead
                    group={id}
                    cases={g.cases.length}
                    findings={g.findings}
                    elsewhere={g.elsewhere}
                    moved={g.moved}
                    reasons={g.reasons}
                    quiet
                    action={g.cases.length ? (
                      <span className="flex items-center gap-[6px]">
                        {isOpen && loose ? <button type="button" disabled={busyWith("organize")} onClick={() => organize(id)} className={`${ACTION_CHIP} disabled:opacity-50`}>Organize these</button> : null}
                        <button type="button" data-investigate-aside={id} onClick={() => setAsideOpen((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n; })} className={ACTION_CHIP}>{isOpen ? "Hide" : "Show"}</button>
                      </span>
                    ) : null}
                  />
                  {isOpen ? listOf(g) : null}
                </div>
              );
            })}
          </section>
        </div>
      )}
    </div>
  );
}
